import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Edit2, Trash2, LogOut, Save, Loader2, Plus, Star } from "lucide-react";
import { api, resolveCoverUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AGE_GROUPS } from "@/lib/constants";
import AddBookModal from "@/components/AddBookModal";
import { toast } from "sonner";

const TAB_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // book object being edited
  const [rejecting, setRejecting] = useState(null); // {book, note}
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const fetchBooks = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/books?status=${status}`);
      setBooks(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/books/${id}/approve`);
      toast.success("Book approved — it's now live on the shelf.");
      await fetchBooks(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting.book.id);
    try {
      await api.post(`/admin/books/${rejecting.book.id}/reject`, {
        note: rejecting.note || "",
      });
      toast.success("Book rejected. Author has been notified.");
      setRejecting(null);
      await fetchBooks(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this book from the database? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/books/${id}`);
      toast.success("Book removed.");
      await fetchBooks(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const toggleFeature = async (b) => {
    setBusyId(b.id);
    try {
      const { data } = await api.post(`/admin/books/${b.id}/feature`);
      toast.success(
        data.featured
          ? `"${b.title}" is now the Featured Book of the Month.`
          : `"${b.title}" removed from Featured.`,
      );
      await fetchBooks(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusyId(editing.id);
    try {
      await api.put(`/admin/books/${editing.id}`, {
        title: editing.title,
        author_name: editing.author_name,
        description: editing.description,
        age_group: editing.age_group,
        purchase_url: editing.purchase_url,
        author_link: editing.author_link,
      });
      toast.success("Changes saved.");
      setEditing(null);
      await fetchBooks(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main className="bg-page min-h-screen" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#CFD6DC] backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-[#0E232E]">
            <img src="/tbp-logo.png" alt="The Bookshelf Project" className="h-10 w-auto" />
            <span className="font-display text-lg font-extrabold hidden md:inline">
              Bookshelf Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-xs text-[#2F2E2E]">
              Signed in as {user?.email}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm font-semibold text-[#2B328C] hover:text-[#0E232E]"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E232E]">Submissions</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setAddOpen(true)}
              className="btn-teal px-4 py-2 rounded-full text-xs uppercase tracking-wider flex items-center gap-1.5"
              data-testid="admin-add-book-btn"
            >
              <Plus size={14} /> Add book
            </button>
            <div className="flex gap-2" role="tablist">
            {TAB_STATUSES.map((s) => (
              <button
                key={s.value}
                role="tab"
                aria-selected={tab === s.value}
                onClick={() => setTab(s.value)}
                className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold transition-all ${
                  tab === s.value
                    ? "bg-[#38B6D2] text-white border border-[#2588A2]"
                    : "bg-white border border-[#CFD6DC] text-[#0E232E] hover:border-[#38B6D2]"
                }`}
                data-testid={`admin-tab-${s.value}`}
              >
                {s.label}
              </button>
            ))}
            </div>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="text-[#0E232E]/60 italic text-lg py-20 text-center">
              Loading submissions…
            </div>
          ) : books.length === 0 ? (
            <div
              className="text-[#0E232E]/60 italic text-lg py-20 text-center border border-dashed border-[#CFD6DC] rounded-xl bg-white"
              data-testid="admin-empty"
            >
              No {tab} submissions at the moment.
            </div>
          ) : (
            <ul className="space-y-4" data-testid="admin-book-list">
              {books.map((b) => (
                <li
                  key={b.id}
                  className="bg-white border border-[#CFD6DC] rounded-xl p-5 flex flex-col md:flex-row gap-5 shadow-sm"
                  data-testid={`admin-book-${b.id}`}
                >
                  <div
                    className="w-24 h-32 flex-shrink-0 rounded-md overflow-hidden border border-[#CFD6DC] bg-[#F0F3F5]"
                    style={{
                      backgroundImage: b.cover_image_url
                        ? `url(${resolveCoverUrl(b.cover_image_url)})`
                        : undefined,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="font-display text-2xl font-extrabold text-[#0E232E] leading-tight flex items-center gap-2 flex-wrap">
                          {b.title}
                          {b.featured && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#F7C94B] text-[#0E232E] px-2 py-0.5 rounded-full font-extrabold border border-[#D4A82E]"
                              data-testid={`featured-badge-${b.id}`}
                            >
                              <Star size={10} fill="currentColor" /> Featured
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-[#2F2E2E] mt-1">
                          by {b.author_name} · {b.author_email}
                        </p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider bg-[#7EC8E3]/20 text-[#0E232E] px-2 py-1 rounded border border-[#7EC8E3]/40 font-bold">
                        {b.age_group_label}
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-[#2F2E2E] leading-relaxed line-clamp-4">
                      {b.description}
                    </p>
                    <div className="mt-3 text-xs text-[#2B328C] break-words">
                      <a href={b.purchase_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#0E232E]">
                        {b.purchase_url}
                      </a>
                    </div>
                    {b.rejection_note && (
                      <div className="mt-2 text-xs text-[#B23B28]">
                        Rejection note: {b.rejection_note}
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      {b.status !== "approved" && (
                        <button
                          onClick={() => approve(b.id)}
                          disabled={busyId === b.id}
                          className="btn-primary px-4 py-1.5 rounded-md text-sm flex items-center gap-1.5"
                          data-testid={`approve-${b.id}`}
                        >
                          <Check size={14} /> Approve
                        </button>
                      )}
                      {b.status === "approved" && (
                        <button
                          onClick={() => toggleFeature(b)}
                          disabled={busyId === b.id}
                          className={`px-4 py-1.5 rounded-md text-sm flex items-center gap-1.5 font-semibold transition-colors ${
                            b.featured
                              ? "bg-[#F7C94B] text-[#0E232E] border border-[#D4A82E] hover:bg-[#E8B833]"
                              : "border border-[#CFD6DC] text-[#0E232E] hover:bg-[#FFF8E7] hover:border-[#F7C94B]"
                          }`}
                          data-testid={`feature-${b.id}`}
                          title={
                            b.featured
                              ? "Remove from Featured"
                              : "Set as Featured Book of the Month"
                          }
                        >
                          <Star size={14} fill={b.featured ? "currentColor" : "none"} />
                          {b.featured ? "Featured" : "Feature"}
                        </button>
                      )}
                      {b.status !== "rejected" && (
                        <button
                          onClick={() => setRejecting({ book: b, note: "" })}
                          disabled={busyId === b.id}
                          className="px-4 py-1.5 rounded-md text-sm flex items-center gap-1.5 border border-[#CFD6DC] text-[#0E232E] font-semibold hover:bg-[#F0F3F5]"
                          data-testid={`reject-${b.id}`}
                        >
                          <X size={14} /> Reject
                        </button>
                      )}
                      <button
                        onClick={() => setEditing({ ...b })}
                        disabled={busyId === b.id}
                        className="px-4 py-1.5 rounded-md text-sm flex items-center gap-1.5 border border-[#CFD6DC] text-[#0E232E] font-semibold hover:bg-[#F0F3F5]"
                        data-testid={`edit-${b.id}`}
                      >
                        <Edit2 size={14} /> Edit
                      </button>
                      <button
                        onClick={() => remove(b.id)}
                        disabled={busyId === b.id}
                        className="px-4 py-1.5 rounded-md text-sm flex items-center gap-1.5 border border-[#B23B28] text-white font-semibold bg-[#B23B28] hover:bg-[#8C2B1E]"
                        data-testid={`delete-${b.id}`}
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(14,35,46,0.55)", backdropFilter: "blur(6px)" }}
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-white border border-[#CFD6DC] rounded-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6 md:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-3xl font-extrabold text-[#0E232E]">Edit submission</h2>
            <div className="mt-5 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Title</label>
                  <input
                    className="field-input"
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    data-testid="edit-title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Author name</label>
                  <input
                    className="field-input"
                    value={editing.author_name}
                    onChange={(e) => setEditing({ ...editing, author_name: e.target.value })}
                    data-testid="edit-author"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Age group</label>
                <select
                  className="field-input"
                  value={editing.age_group}
                  onChange={(e) => setEditing({ ...editing, age_group: e.target.value })}
                  data-testid="edit-age-group"
                >
                  {AGE_GROUPS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label} ({g.range})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Description</label>
                <textarea
                  rows={5}
                  className="field-input"
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  data-testid="edit-description"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Purchase URL</label>
                <input
                  className="field-input"
                  value={editing.purchase_url}
                  onChange={(e) => setEditing({ ...editing, purchase_url: e.target.value })}
                  data-testid="edit-purchase-url"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Author link (optional)</label>
                <input
                  className="field-input"
                  value={editing.author_link || ""}
                  onChange={(e) => setEditing({ ...editing, author_link: e.target.value })}
                  data-testid="edit-author-link"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-md border border-[#CFD6DC] text-[#0E232E] font-semibold hover:bg-[#F0F3F5]"
                data-testid="edit-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={busyId === editing.id}
                className="btn-primary px-5 py-2 rounded-md flex items-center gap-2"
                data-testid="edit-save"
              >
                {busyId === editing.id ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejecting && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(14,35,46,0.55)", backdropFilter: "blur(6px)" }}
          onClick={() => setRejecting(null)}
        >
          <div
            className="bg-white border border-[#CFD6DC] rounded-xl max-w-lg w-full p-6 md:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-2xl font-extrabold text-[#0E232E]">
              Reject "{rejecting.book.title}"?
            </h2>
            <p className="mt-2 text-sm text-[#2F2E2E]">
              A polite email will be sent to <strong>{rejecting.book.author_email}</strong>.
              Add an optional note for the author below.
            </p>
            <textarea
              rows={4}
              className="field-input mt-4"
              placeholder="Optional note (e.g. 'Please re-submit with a higher-resolution cover.')"
              value={rejecting.note}
              onChange={(e) => setRejecting({ ...rejecting, note: e.target.value })}
              data-testid="reject-note"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setRejecting(null)}
                className="px-4 py-2 rounded-md border border-[#CFD6DC] text-[#0E232E] font-semibold hover:bg-[#F0F3F5]"
                data-testid="reject-cancel"
              >
                Cancel
              </button>
              <button
                onClick={doReject}
                disabled={busyId === rejecting.book.id}
                className="btn-primary px-5 py-2 rounded-md flex items-center gap-2"
                data-testid="reject-confirm"
              >
                {busyId === rejecting.book.id && <Loader2 className="animate-spin" size={16} />}
                Send rejection
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add book modal */}
      <AddBookModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => fetchBooks(tab)} />
    </main>
  );
}
